import React, { useState } from "react";
import { Link } from "react-router-dom";
import { money } from "../dashboardHelpers";

// Top-row summary tile - icon avatar + label + big value + an optional
// trend line ("vs Yesterday +12.5%") or a plain caption underneath. Trend
// direction is colored (green up / red down), not assumed - a KPI where
// "up" is bad (e.g. a future overdue count) should pass its own caption
// instead of `trendPercent`.
//
// Comparison handling (section 8/9): passing `trendLabel` means "a
// comparison was attempted" - if `trendPercent` comes back null (the
// comparison period genuinely has no data, e.g. Collections 5 years back
// before this branch existed), that's shown honestly as "No comparison
// data yet" rather than silently disappearing or showing a fake 0%/∞%.
// A snapshot widget that never passes `trendLabel` renders exactly as
// before - this is a backward-compatible extension, not a new component.
//
// When `currentValue`/`comparisonValue` are both supplied, the trend line
// becomes clickable to reveal the Current/Comparison/Difference/Change
// breakdown (section 9) - optional, so a widget that only has the % and
// not the raw figures still works unchanged.
// `drillTo` (2026-09-16) makes the VALUE a link to the records behind it. It
// deliberately goes on the value and not the whole card: the trend line's own
// click is already the comparison-breakdown toggle, and a card-wide link would
// swallow it. `drillTitle` is the hover text explaining where it goes - always
// say what the target is filtered to, since landing on a list that looks
// unrelated is worse than no link.
const KpiCard = ({
  icon, iconBg, label, value, trendLabel, trendPercent, caption,
  currentValue, comparisonValue, compareRangeLabel, drillTo, drillTitle,
}) => {
  const [expanded, setExpanded] = useState(false);
  const hasComparisonAttempt = Boolean(trendLabel);
  const hasBreakdown = currentValue !== undefined && comparisonValue !== undefined && comparisonValue !== null;
  const difference = hasBreakdown ? Number(currentValue) - Number(comparisonValue) : null;

  return (
    <div className="card h-100">
      <div className="card-body">
        <div className="d-flex align-items-center gap-2 mb-2">
          <span
            className="d-inline-flex align-items-center justify-content-center rounded-circle"
            style={{ width: 40, height: 40, backgroundColor: iconBg || "#6E1423", color: "#fff", flexShrink: 0 }}
          >
            {icon}
          </span>
          <span className="text-muted small text-uppercase fw-semibold">{label}</span>
        </div>
        <div className="fs-4 fw-bold mb-1">
          {drillTo ? (
            <Link to={drillTo} title={drillTitle || "Open the records behind this figure"} className="text-decoration-none">
              {value}
            </Link>
          ) : (
            value
          )}
        </div>

        {hasComparisonAttempt && trendPercent !== undefined && trendPercent !== null && (
          <div
            className={`small ${hasBreakdown ? "cursor-pointer" : ""}`}
            style={hasBreakdown ? { cursor: "pointer" } : undefined}
            onClick={hasBreakdown ? () => setExpanded((e) => !e) : undefined}
            title={hasBreakdown ? "Click to see the comparison breakdown" : undefined}
          >
            <span className="text-muted">{trendLabel} </span>
            <span className={trendPercent >= 0 ? "text-success fw-semibold" : "text-danger fw-semibold"}>
              {trendPercent >= 0 ? "↑" : "↓"} {Math.abs(trendPercent).toFixed(1)}%
            </span>
          </div>
        )}
        {hasComparisonAttempt && (trendPercent === undefined || trendPercent === null) && (
          <div className="small text-muted fst-italic">No comparison data yet{compareRangeLabel ? ` (${compareRangeLabel})` : ""}</div>
        )}
        {caption && <div className="small text-muted">{caption}</div>}

        {expanded && hasBreakdown && (
          <div className="mt-2 pt-2 border-top small">
            <div className="d-flex justify-content-between"><span className="text-muted">Current Period</span><span className="fw-semibold">{money(currentValue)}</span></div>
            <div className="d-flex justify-content-between"><span className="text-muted">Comparison Period</span><span className="fw-semibold">{money(comparisonValue)}</span></div>
            <div className="d-flex justify-content-between">
              <span className="text-muted">Difference</span>
              <span className={`fw-semibold ${difference >= 0 ? "text-success" : "text-danger"}`}>
                {difference >= 0 ? "+" : ""}{money(difference)}
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default KpiCard;
